import { create } from "zustand";
import { apiRequest } from "@/lib/api";

export type TaskStatus = "todo" | "in_progress" | "done";
export type TaskPriority = "low" | "medium" | "high";
export type TaskTimeframe = "week" | "month" | "quarter";

export interface Task {
  id: string;
  document_id: string | null;
  title: string;
  description: string;
  completed: boolean;
  status: TaskStatus;
  priority: TaskPriority;
  timeframe: TaskTimeframe;
  assignee_id: string | null;
  assignee_name: string | null;
  created_by: string | null;
  due_date: string | null;
  completed_at: string | null;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface TaskSummary {
  total: number;
  todo: number;
  in_progress: number;
  done: number;
  by_priority: Record<string, number>;
  by_assignee: { assignee_id: string | null; name: string; count: number }[];
  by_project: { project_id: string | null; name: string; count: number }[];
}

interface TasksState {
  tasks: Task[];
  summary: TaskSummary | null;
  loading: boolean;
  error: string | null;

  fetchTasks: (filters?: {
    document_id?: string;
    assignee_id?: string;
    status?: TaskStatus;
    project_id?: string;
    completed?: boolean;
  }) => Promise<void>;
  fetchSummary: () => Promise<void>;
  createTask: (input: {
    document_id?: string | null;
    title: string;
    assignee_id?: string | null;
    priority?: TaskPriority;
    timeframe?: TaskTimeframe;
  }) => Promise<Task>;
  updateTask: (
    id: string,
    patch: Partial<{
      title: string;
      completed: boolean;
      status: TaskStatus;
      priority: TaskPriority;
      timeframe: TaskTimeframe;
      assignee_id: string | null;
      position: number;
    }>
  ) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  clearTasks: () => void;
}

export const useTasksStore = create<TasksState>((set, get) => ({
  tasks: [],
  summary: null,
  loading: false,
  error: null,

  fetchTasks: async (filters = {}) => {
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters.document_id) params.set("document_id", filters.document_id);
      if (filters.assignee_id) params.set("assignee_id", filters.assignee_id);
      if (filters.status) params.set("status", filters.status);
      if (filters.project_id) params.set("project_id", filters.project_id);
      if (filters.completed !== undefined) params.set("completed", String(filters.completed));
      const qs = params.toString() ? `?${params.toString()}` : "";

      const res = await apiRequest<{ items: Task[]; total: number; offset: number; limit: number }>(
        `/v1/tasks${qs}`
      );
      set({ tasks: res.items || [], loading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Error al cargar tareas",
        loading: false,
      });
    }
  },

  fetchSummary: async () => {
    try {
      const summary = await apiRequest<TaskSummary>("/v1/tasks/summary");
      set({ summary });
    } catch {
      // Summary is optional; fail silently
    }
  },

  createTask: async (input) => {
    const created = await apiRequest<Task>("/v1/tasks", {
      method: "POST",
      body: JSON.stringify({
        document_id: input.document_id ?? null,
        title: input.title,
        assignee_id: input.assignee_id ?? null,
        priority: input.priority ?? "medium",
        timeframe: input.timeframe ?? "week",
      }),
    });
    set((s) => ({ tasks: [...s.tasks, created] }));
    get().fetchSummary();
    return created;
  },

  updateTask: async (id, patch) => {
    // Optimistic update
    set((s) => ({
      tasks: s.tasks.map((t) => {
        if (t.id !== id) return t;
        const next = { ...t, ...patch };
        if ("status" in patch && patch.status) {
          next.completed = patch.status === "done";
          next.completed_at = patch.status === "done" ? new Date().toISOString() : null;
        }
        if ("completed" in patch && typeof patch.completed === "boolean") {
          next.status = patch.completed ? "done" : "todo";
          next.completed_at = patch.completed ? new Date().toISOString() : null;
        }
        return next;
      }),
    }));

    try {
      await apiRequest(`/v1/tasks/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      get().fetchSummary();
    } catch (err) {
      // Revert on failure — re-fetch from server
      get().fetchTasks();
      throw err;
    }
  },

  deleteTask: async (id) => {
    const prev = get().tasks;
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
    try {
      await apiRequest(`/v1/tasks/${id}`, { method: "DELETE" });
      get().fetchSummary();
    } catch (err) {
      set({ tasks: prev });
      throw err;
    }
  },

  clearTasks: () => set({ tasks: [], summary: null, error: null }),
}));